import { FaArrowLeftLong } from "react-icons/fa6";
import { LuLoader } from "react-icons/lu";
import { clsxm } from "@/utils/clsxm";
import { useQuizContext } from "@/pages/quiz/context";

interface IProps {
  // DESC: temporary decision for AB test 4.1 header
  onClick?: () => void;
  disabled?: boolean;
  type?: ButtonType;
  version?: "v1" | "v2" | "v3";
  className?: string;
  btnClassName?: string;
  showOnlyBackButton?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
  label?: string;
  // Keep the button full-width even when a back button is shown
  fullWidth?: boolean;
}

export enum ButtonType {
  NONE = "NONE",
  NEXT = "NEXT",
  CONTINUE = "CONTINUE",
  SUBMIT = "SUBMIT",
  TRY_AGAIN = "TRY_AGAIN",
}

const labelMap: { [key: string]: string } = {
  NEXT: "Next",
  CONTINUE: "Continue",
  SUBMIT: "Submit",
  TRY_AGAIN: "Try again",
};

const Button = ({
  onClick,
  type = ButtonType.CONTINUE,
  disabled = false,
  version = "v1",
  className,
  btnClassName = "",
  showOnlyBackButton = false,
  isLoading = false,
  loadingLabel,
  label,
  fullWidth = false,
}: IProps) => {
  const { onBack, selectedQuestionIdx } = useQuizContext();

  const buttonLabel = label ?? labelMap[type];

  if (type === ButtonType.NONE) {
    return null;
  }

  const showBackButton = selectedQuestionIdx >= 1;
  // The compact style is only for the inline next-button beside a back button.
  const useCompactStyle = showBackButton && !fullWidth;

  if (version === "v3") {
    return (
      <div
        className={clsxm(
          "mt-6 flex w-full items-center justify-between gap-4",
          className
        )}
      >
        {showBackButton && (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1 rounded-full bg-white px-1 text-base font-medium text-main-button-v2 transition-all hover:cursor-pointer hover:bg-silver-2"
          >
            <FaArrowLeftLong />
            Back
          </button>
        )}

        {!showOnlyBackButton &&
          (type === ButtonType.NEXT ||
            type === ButtonType.CONTINUE ||
            type === ButtonType.SUBMIT) && (
            <button
              type="button"
              onClick={onClick}
              disabled={disabled}
              className={clsxm(
                "ml-auto rounded-full bg-white px-1 text-base font-medium text-main-button-v2 transition-all hover:cursor-pointer hover:bg-silver-2 disabled:cursor-not-allowed disabled:opacity-50",
                btnClassName
              )}
            >
              {label ?? labelMap[type]}
            </button>
          )}
      </div>
    );
  }

  const btn = (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={clsxm(
        "rounded-full w-full min-h-12 text-base",
        version === "v1"
          ? "action-button_yellow font-bold text-xl p-[16px_60px] sm:p-[20px_70px]"
          : "bg-accent-button-v2 text-white font-semibold rounded-full w-full p-[8px_60px] disabled:bg-accent-button-v2/70",
        useCompactStyle &&
          "!bg-purple10 text-white max-w-[160px] !px-4 !py-2 !text-base font-medium sm:max-w-[200px] disabled:opacity-50 disabled:!text-white",
        btnClassName
      )}
    >
      {isLoading ? (
        <span className="flex items-center justify-center gap-2">
          <LuLoader className="h-5 w-5 animate-spin" />
          {loadingLabel ?? buttonLabel}
        </span>
      ) : (
        buttonLabel
      )}
    </button>
  );

  if (version === "v2") {
    return (
      <div
        className={clsxm(
          // "fixed bottom-16 left-0 right-0",
          "z-20 flex w-full items-center gap-2",
          "relative !mt-4 sm:gap-24 sm:px-0 md:!mt-6",
          className
        )}
      >
        {btn}
      </div>
    );
  }

  return (
    <div
      className={clsxm(
        "w-full flex items-center justify-center !mt-[24px] sm:!mt-[28px] lg:!mt-[30px]",
        showBackButton && "relative min-h-6",
        className
      )}
    >
      {showBackButton && (
        <button
          className={clsxm(
            "flex items-center gap-1 top-0 left-0 px-1 bg-white z-10 text-main-button-v2 rounded-full transition-all font-medium",
            "absolute top-1/2 transform -translate-y-1/2",
            "hover:cursor-pointer hover:bg-silver-2"
          )}
          onClick={onBack}
        >
          <FaArrowLeftLong />
          Back
        </button>
      )}

      {!showOnlyBackButton &&
        (type === ButtonType.NEXT ||
          type === ButtonType.CONTINUE ||
          type === ButtonType.SUBMIT ||
          type === ButtonType.TRY_AGAIN) &&
        btn}
    </div>
  );
};

export { Button };
